// Bundled help-center search index for the in-app Help drawer.
//
// ⚠️ CANONICAL SOURCE: the tenfour-site repo's `src/help/content.mjs` — the
// marketing site renders the full articles at tenfour.chat/help. This file is
// a hand-synced index (slug/title/description/keywords only) so the drawer's
// search is instant, offline-safe, and needs no CORS. When an article is
// added/renamed on the site, mirror the index entry here in the same change.
// (plans/help-support-status.md)

export const MARKETING_SITE_URL: string =
  (import.meta.env.VITE_MARKETING_SITE_URL as string | undefined)?.replace(/\/$/, '') ??
  'https://tenfour.chat'

export interface HelpCategory {
  slug: string
  label: string
}

export interface HelpArticle {
  slug: string
  category: string
  title: string
  description: string
  /** Extra search terms beyond title/description words. */
  keywords: string[]
}

export const HELP_CATEGORIES: HelpCategory[] = [
  { slug: 'getting-started', label: 'Getting started' },
  { slug: 'your-number', label: 'Your Ten Four number' },
  { slug: 'messaging', label: 'Messaging & AI' },
  { slug: 'invoices-payments', label: 'Invoices & payments' },
  { slug: 'billing-plans', label: 'Billing & plans' },
  { slug: 'account-settings', label: 'Account & settings' },
]

export const HELP_ARTICLES: HelpArticle[] = [
  // ── Getting started ──────────────────────────────────────────────────
  {
    slug: 'welcome-to-ten-four',
    category: 'getting-started',
    title: 'What Ten Four is and how it works',
    description: 'Your business number, answered — how the service handles calls and texts for you.',
    keywords: ['intro', 'overview', 'start', 'how it works', 'concierge'],
  },
  {
    slug: 'set-up-your-account',
    category: 'getting-started',
    title: 'Set up your account',
    description: 'Business info, your Ten Four number, and connecting payments — the 10-minute setup.',
    keywords: ['setup', 'onboarding', 'signup', 'connect stripe', 'business name'],
  },
  {
    slug: 'your-first-booking',
    category: 'getting-started',
    title: 'Your first booking, start to finish',
    description: 'What happens when a customer texts: the conversation, the job, and what needs your attention.',
    keywords: ['booking', 'job', 'queue', 'first customer', 'schedule'],
  },
  // ── Your Ten Four number ─────────────────────────────────────────────
  {
    slug: 'how-your-number-works',
    category: 'your-number',
    title: 'How your Ten Four number works',
    description: 'One business number for calls and texts, separate from your personal phone.',
    keywords: ['phone number', 'twilio', 'business line', 'texting number'],
  },
  {
    slug: 'use-your-existing-number',
    category: 'your-number',
    title: 'Keep your existing number',
    description: 'Porting a number you already advertise, and what to expect during the move.',
    keywords: ['port', 'porting', 'transfer number', 'existing number', 'keep number'],
  },
  {
    slug: 'voice-calls',
    category: 'your-number',
    title: 'How Ten Four answers your calls',
    description: 'The voice assistant, when calls forward to you, and where transcripts live.',
    keywords: ['calls', 'voice', 'answering', 'voicemail', 'transcript', 'forward'],
  },
  // ── Messaging & AI ───────────────────────────────────────────────────
  {
    slug: 'how-the-assistant-texts',
    category: 'messaging',
    title: 'How the assistant texts your customers',
    description: 'What the AI says, what it never does without you, and how jobs get booked over SMS.',
    keywords: ['ai', 'assistant', 'lyra', 'sms', 'texting', 'auto reply'],
  },
  {
    slug: 'jump-into-a-conversation',
    category: 'messaging',
    title: 'Jumping into a conversation yourself',
    description: 'Take over any thread from Messages — the assistant steps back while you type.',
    keywords: ['take over', 'reply myself', 'messages', 'manual', 'conversation'],
  },
  {
    slug: 'customer-memory',
    category: 'messaging',
    title: 'What Ten Four remembers about customers',
    description: 'Names, addresses, past jobs, and preferences — and how to view or correct them.',
    keywords: ['memory', 'customer notes', 'history', 'crm', 'details'],
  },
  // ── Invoices & payments ──────────────────────────────────────────────
  {
    slug: 'how-invoices-work',
    category: 'invoices-payments',
    title: 'How invoices work',
    description: 'Drafted from the job, reviewed by a human, confirmed by you with a YES — then sent.',
    keywords: ['invoice', 'review', 'confirm', 'draft', 'approval', 'yes'],
  },
  {
    slug: 'getting-paid',
    category: 'invoices-payments',
    title: 'Getting paid',
    description: 'Stripe payment links, cash and check recording, payouts, and reminders.',
    keywords: ['payment', 'stripe', 'payout', 'pay link', 'card', 'reminder'],
  },
  {
    slug: 'sales-tax',
    category: 'invoices-payments',
    title: 'How sales tax is applied',
    description: 'The one toggle, the job-location rate lookup, and the audit line on every invoice.',
    keywords: ['tax', 'sales tax', 'rate', 'taxable', 'zip'],
  },
  // ── Billing & plans ──────────────────────────────────────────────────
  {
    slug: 'plans-and-pricing',
    category: 'billing-plans',
    title: 'Plans and what is included',
    description: 'Growth, Pro, and Max — features, allowances, and monthly pricing.',
    keywords: ['plan', 'tier', 'pricing', 'subscription', 'pro', 'max', 'upgrade'],
  },
  {
    slug: 'usage-and-allowances',
    category: 'billing-plans',
    title: 'Usage, allowances, and caps',
    description: 'Voice minutes, text counts, the AI budget, and what happens when you hit a cap.',
    keywords: ['usage', 'allowance', 'cap', 'limit', 'overage', 'manual mode'],
  },
  {
    slug: 'upgrade-downgrade',
    category: 'billing-plans',
    title: 'Upgrading or downgrading your plan',
    description: 'Self-serve upgrades take effect immediately; downgrades at your next cycle.',
    keywords: ['upgrade', 'downgrade', 'change plan', 'cancel', 'billing'],
  },
  // ── Account & settings ───────────────────────────────────────────────
  {
    slug: 'team-members',
    category: 'account-settings',
    title: 'Adding team members',
    description: 'Invite teammates, set what they can do, and route new-job alerts to the crew.',
    keywords: ['team', 'invite', 'member', 'roles', 'permissions'],
  },
  {
    slug: 'notifications',
    category: 'account-settings',
    title: 'Notification preferences',
    description: 'Choose text or email per event, and the daily recap digest.',
    keywords: ['notifications', 'alerts', 'digest', 'email', 'preferences'],
  },
  {
    slug: 'security',
    category: 'account-settings',
    title: 'Passwords, passkeys, and 2FA',
    description: 'Keeping your account secure — sign-in options and two-factor authentication.',
    keywords: ['security', 'password', 'passkey', '2fa', 'two factor', 'sso'],
  },
]

const BY_SLUG = new Map(HELP_ARTICLES.map((a) => [a.slug, a]))
const CATEGORY_LABELS = new Map(HELP_CATEGORIES.map((c) => [c.slug, c.label]))

export function helpArticle(slug: string): HelpArticle | undefined {
  return BY_SLUG.get(slug)
}

export function helpCategoryLabel(slug: string): string {
  return CATEGORY_LABELS.get(slug) ?? slug
}

export function helpArticleUrl(article: HelpArticle): string {
  return `${MARKETING_SITE_URL}/help/${article.category}/${article.slug}`
}

export const HELP_CENTER_URL = `${MARKETING_SITE_URL}/help`
export const STATUS_PAGE_URL = `${MARKETING_SITE_URL}/status`

/** Simple ranked substring search over title/description/keywords/category. */
export function searchHelpArticles(query: string, limit = 8): HelpArticle[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const terms = q.split(/\s+/).filter(Boolean)
  const scored: Array<{ article: HelpArticle; score: number }> = []
  for (const article of HELP_ARTICLES) {
    const title = article.title.toLowerCase()
    const description = article.description.toLowerCase()
    const keywords = article.keywords.join(' ').toLowerCase()
    const category = helpCategoryLabel(article.category).toLowerCase()
    let score = 0
    for (const term of terms) {
      if (title.includes(term)) score += title.startsWith(term) ? 5 : 3
      else if (keywords.includes(term)) score += 2
      else if (description.includes(term)) score += 1
      else if (category.includes(term)) score += 1
      else {
        score = 0
        break // every term must match somewhere
      }
    }
    if (score > 0) scored.push({ article, score })
  }
  scored.sort((a, b) => b.score - a.score)
  return scored.slice(0, limit).map((s) => s.article)
}
